'use client'

import type { ReactNode } from 'react'

import { signUpCtaLocationForTab } from '@/helpers/telemetry/signupLinkTracking'
import { trackSignUpStart } from '@/helpers/telemetry/trackSignUpStart'

import { SignInAboutAccordion } from './content/signInAboutAccordion'
import {
  TextBold,
  StyledParagraph,
  StyledInnerParagraph,
  StyledLink,
  List,
  SubList,
  ListItem,
} from './content/ui'

// ── Section data ─────────────────────────────────────────────────────────────

/** First accordion title; legacy `sections` and CMS `aboutIntro` fold use the same label. */
const COSTCO_ABOUT_FIRST_FOLD_TITLE = 'About Costco Wholesale Liquidations'

const sections: { title: string; content: ReactNode }[] = [
  {
    title: COSTCO_ABOUT_FIRST_FOLD_TITLE,
    content: (
      <>
        <StyledParagraph>
          The Costco Wholesale marketplace is a B2B marketplace intended for
          those businesses involved in wholesale and/or resale selling their
          merchandise in-store, online through a company website, and/or via
          third-party seller platforms.
        </StyledParagraph>
        <StyledParagraph>
          Costco Wholesale Liquidations is the official B2B liquidation
          marketplace of the nation&apos;s top membership-only warehouse club,
          Costco Wholesale. Selling palletized apparel, automotive, major
          appliances, computers &amp; tablets, consumer electronics, cookware,
          domestics, food &amp; sundries, footwear, garden &amp; patio,
          handbags, hardware, health &amp; beauty, home furnishings, jewelry
          &amp; watches, luggage, mixed lots, rugs, seasonal, toys, sporting
          goods and more.
        </StyledParagraph>
        <StyledParagraph>
          Apply to{' '}
          <StyledLink href="https://bstock.com/buy/seller/costco">
            Costco Wholesale Liquidations
          </StyledLink>{' '}
          in order to buy Costco liquidation inventory. Please take a moment to
          review the <TextBold>Marketplace Buyer Restrictions</TextBold> to
          understand the buyer approval process for Costco Wholesale
          Liquidations. Only approved buyers can view Costco auctions.
        </StyledParagraph>
      </>
    ),
  },
  {
    title: 'Product Categories',
    content: (
      <>
        <StyledParagraph>
          Costco Wholesale Liquidations offers a wide range of categories of
          liquidation merchandise. Here&apos;s a list of typical inventory
          categories sold on Costco Wholesale Liquidations:
        </StyledParagraph>
        <List>
          <ListItem>
            Apparel
            <SubList>
              <ListItem>Men&apos;s Apparel</ListItem>
              <ListItem>Women&apos;s Apparel</ListItem>
              <ListItem>Girls Apparel</ListItem>
              <ListItem>Boys Apparel</ListItem>
            </SubList>
          </ListItem>
          <ListItem>Computers &amp; Tablets</ListItem>
          <ListItem>Consumer Electronics</ListItem>
          <ListItem>Cookware</ListItem>
          <ListItem>Domestics</ListItem>
          <ListItem>Food &amp; Sundries</ListItem>
          <ListItem>Footwear</ListItem>
          <ListItem>Garden / Patio</ListItem>
          <ListItem>Handbags</ListItem>
          <ListItem>Hardware</ListItem>
          <ListItem>Health &amp; Beauty</ListItem>
          <ListItem>Home Furnishings</ListItem>
          <ListItem>Luggage</ListItem>
          <ListItem>Major Appliances</ListItem>
          <ListItem>Mixed Lots</ListItem>
          <ListItem>Rugs</ListItem>
          <ListItem>Small Appliances</ListItem>
          <ListItem>Sporting Goods</ListItem>
          <ListItem>Toys</ListItem>
        </List>
      </>
    ),
  },
  {
    title: 'Types of Liquidated Inventory',
    content: (
      <>
        <StyledParagraph>
          The selection, quality, and quantity of available products liquidated
          through Costco Wholesale Liquidations depend on a number of factors
          including: returns, shelf pulls and excess inventory.
        </StyledParagraph>
        <List>
          <ListItem>
            <TextBold>Member Returns</TextBold>
            <StyledInnerParagraph>
              Inventory that was purchased by customers and returned to the
              store for any of a number of reasons. The quality of inventory is
              dependent on the amount of use by the customer before returning
              and whether it was damaged or worn out in the process. Typically,
              Member Returns product shows little to no damage.
            </StyledInnerParagraph>
          </ListItem>
          <ListItem>
            <TextBold>Excess Inventory</TextBold>
            <StyledInnerParagraph>
              Inventory that did not sell and is in too small a quantity to
              place on typical Costco displays.
            </StyledInnerParagraph>
          </ListItem>
          <ListItem>
            <TextBold>Rare Shelf Pulls</TextBold>
            <StyledInnerParagraph>
              Inventory that is not in sellable condition directly to customers.
              The merchandise may or may not be functional with missing or
              broken parts.
            </StyledInnerParagraph>
          </ListItem>
        </List>
      </>
    ),
  },
  {
    title: 'Liquidation Lot Sizes',
    content: (
      <>
        <StyledParagraph>
          Costco Wholesale Liquidation Auctions typically sell liquidated
          merchandise in a variety of lot sizes. Generally speaking, auctions
          are most often sold by the pallet. Typical lot sizes include one to
          ten pallet lots (also known as LTL or Less Than Truckload) and
          truckloads.
        </StyledParagraph>
        <StyledParagraph>Buying by the Pallet:</StyledParagraph>
        <List>
          <ListItem>
            Packaged inventory that can fit on an individual pallet, the
            smallest unit of retail storage efficiency. A pallet may also be
            packaged as oversized or double stacked.
          </ListItem>
          <ListItem>
            Typically a pallet is the classic wood construction but can also
            include plastic, metal or recycled material. A pallet may also
            rarely contain items that are packaged in boxes (such as for iPads
            or very high-end designer bags).
          </ListItem>
        </List>
        <StyledParagraph>Common Lot Sizes:</StyledParagraph>
        <List>
          <ListItem>
            LTL (Less Than Truckload)
            <SubList>
              <ListItem>
                A relatively small amount of liquidated freight which is defined
                as between 1&ndash;10 pallet spaces on a truck.
              </ListItem>
            </SubList>
          </ListItem>
          <ListItem>
            Full Truckloads
            <SubList>
              <ListItem>
                A truckload of liquidated inventory is typically defined as
                containing 11 or more pallet spaces of inventory.
              </ListItem>
              <ListItem>
                You can combine multiple LTL auctions to create a full truckload
                and save on shipping costs under certain circumstances. Please
                see the corresponding FAQ for more information.
              </ListItem>
            </SubList>
          </ListItem>
          <ListItem>
            Small Pack (Boxes):
            <SubList>
              <ListItem>
                A small amount of liquidated freight which is less than 1 pallet
                quantities, typically shipped in boxed and featuring free
                shipping.
              </ListItem>
            </SubList>
          </ListItem>
        </List>
      </>
    ),
  },
  {
    title:
      'Buy from Locations Near your Business to Save Time and Shipping Costs',
    content: (
      <>
        <StyledParagraph>
          Individual auction listings highlight the location that the inventory
          in that listing will be shipped from. Auctions are closing daily,
          typically from the following locations:
        </StyledParagraph>
        <List>
          <ListItem>Atlanta, GA</ListItem>
          <ListItem>Carrollton, TX</ListItem>
          <ListItem>Dallas, TX</ListItem>
          <ListItem>Frederick, MD</ListItem>
          <ListItem>Fresno, CA</ListItem>
          <ListItem>Mira Loma, CA</ListItem>
          <ListItem>Monroe Township, NJ</ListItem>
          <ListItem>Morris, IL</ListItem>
          <ListItem>Salt Lake City, UT</ListItem>
          <ListItem>Sumner, WA</ListItem>
          <ListItem>Tolleson, AZ</ListItem>
          <ListItem>Tracy, CA</ListItem>
          <ListItem>Van Buren Township, MI</ListItem>
          <ListItem>West Palm Beach, FL</ListItem>
        </List>
      </>
    ),
  },
  {
    title: 'Marketplace Buyer Requirements',
    content: (
      <>
        <StyledParagraph>
          In order to view and bid on Costco auction listings, you must be an
          approved buyer. To successfully apply to the marketplace, you must
          provide A) a valid{' '}
          <StyledLink href="/faq/buyer-faq/applying-logging-in/how-do-i-get-a-resale-certificate/">
            state-specific resale certificate
          </StyledLink>{' '}
          for US buyers <TextBold>OR</TextBold> B) a Proof of Business for
          international buyers, when applying for marketplace approval. Please
          ensure that you provide a valid US address, as shipment must be
          accepted in the US.
        </StyledParagraph>
        <List>
          <ListItem>
            We recommend that you take a moment to review the list of
            state-specific resale forms by each state, as well as additional
            information on reseller certificates.
          </ListItem>
          <ListItem>
            Buyers must not resell inventory purchased from Costco Wholesale
            Liquidation Auctions within a 5 mile radius of a Costco retail
            store.
          </ListItem>
        </List>
      </>
    ),
  },
  {
    title: 'Additional Resources',
    content: (
      <>
        <StyledParagraph>
          If you have additional questions about Costco Wholesale Liquidations,
          please feel free to{' '}
          <StyledLink href="/contact-us/">
            <TextBold>Contact Us</TextBold>
          </StyledLink>
          !
        </StyledParagraph>
        <StyledParagraph>
          To join the Costco Wholesale Liquidations marketplace, please{' '}
          <StyledLink
            href="/acct/signup?siteAbb=cst"
            onClick={() =>
              trackSignUpStart(signUpCtaLocationForTab('costco', 'about'))
            }
          >
            apply here
          </StyledLink>
          !
        </StyledParagraph>
      </>
    ),
  },
]

/**
 * Costco's hardcoded sign-in About, kept whole until the port to the CMS deletes it. Shown while
 * Costco's storefront id is in the legacy `aboutEnabledStorefrontIds` allowlist; CMS-authored
 * sign-in content for any storefront renders through `SignInAbout` instead.
 */
export function CostcoAbout() {
  return (
    <SignInAboutAccordion
      items={sections.map(({ title, content }) => ({
        key: title,
        title,
        content,
      }))}
    />
  )
}
